import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DEFAULT_LANG, SUPPORTED_LANGS, SupportedLang } from '@app/core/i18n/supported-langs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { LanguageService } from '@app/core/services/language-service';

@Component({
  selector: 'app-language-selector',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: 'language-selector.html',
  styleUrl: 'language-selector.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSelector {
  private readonly translate = inject(TranslateService);
  private readonly languageService = inject(LanguageService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly supportedLangs = SUPPORTED_LANGS;
  readonly menuId = 'language-selector-options';
  readonly isOpen = signal(false);
  readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translate.getCurrentLang() ?? DEFAULT_LANG,
  });
  readonly availableLangs = computed(() =>
    this.supportedLangs.filter((lang) => lang !== this.currentLang()),
  );

  @ViewChild('trigger') private readonly trigger?: ElementRef<HTMLButtonElement>;

  toggleMenu(): void {
    if (this.availableLangs().length === 0) return;
    this.isOpen.update((isOpen) => !isOpen);
  }

  selectLang(lang: string): void {
    this.setLang(lang);
    this.closeMenu();
  }

  setLang(lang: SupportedLang): void {
    this.languageService.setLang(lang);
  }

  @HostListener('document:click', ['$event'])
  closeOnOutsideClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeMenu();
    }
  }

  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    if (!this.isOpen()) return;
    this.closeMenu();
    this.trigger?.nativeElement.focus();
  }

  private closeMenu(): void {
    this.isOpen.set(false);
  }
}
